import { useEffect, useState, useCallback, useMemo } from "react";
import { Polygon } from "react-leaflet";
import LeafletMap, { FitBounds, polygonCentroid } from "@/components/map/LeafletMap";
import BusinessLayers from "@/components/map/BusinessLayers";
import HeatLayer from "@/components/map/HeatLayer";
import { BusinessLayerToggles } from "@/components/map/BusinessLayerToggles";
import { api, apiError } from "@/lib/api";import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Search, MapPin, Sparkles, Info, Check, Route } from "lucide-react";
import { ShopAreaRequest } from "@/components/ZoneDiscovery";
import { fmtMoney, scoreColor } from "@/lib/prospects";
import { classifyPoint } from "@/lib/geoClassify";
import { toast } from "sonner";

const ALL = "__all__";

export default function MarketplacePanel({ claimMode = false, onClaimed }) {
  const [data, setData] = useState({ items: [], states: [], classifications: [] });
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState(ALL);
  const [cls, setCls] = useState(ALL);
  const [q, setQ] = useState("");
  const [mapZone, setMapZone] = useState(null);
  const [requesting, setRequesting] = useState(null);
  const [claimZone, setClaimZone] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [zoneBiz, setZoneBiz] = useState(null);
  const [bizLoading, setBizLoading] = useState(false);
  const [layers, setLayers] = useState({ restaurant: true, bike_shop: true, coffee: true, brewery: true, hotel: true });
  const [heatOn, setHeatOn] = useState(false);

  useEffect(() => {
    if (!mapZone) { setZoneBiz(null); return; }
    let active = true;
    setBizLoading(true); setZoneBiz(null);
    setLayers({ restaurant: true, bike_shop: true, coffee: true, brewery: true, hotel: true });
    setHeatOn(false);
    api.get(`/shop/marketplace/${mapZone.zoneId}/businesses`)
      .then(({ data }) => { if (active) setZoneBiz(data); })
      .catch(() => { if (active) setZoneBiz({ businesses: [], counts: {}, total: 0, mappedCount: 0, missingCount: 0 }); })
      .finally(() => { if (active) setBizLoading(false); });
    return () => { active = false; };
  }, [mapZone]);

  const allBiz = useMemo(() => (zoneBiz?.businesses || []).filter((b) => b.lat != null && b.lng != null), [zoneBiz]);
  const visibleBiz = useMemo(() => allBiz.filter((b) => layers[b.layer]), [allBiz, layers]);
  const bizCounts = useMemo(() => {
    const c = { restaurant: 0, bike_shop: 0, coffee: 0, brewery: 0, hotel: 0 };
    allBiz.forEach((b) => { if (c[b.layer] != null) c[b.layer] += 1; });
    return c;
  }, [allBiz]);
  const heatPoints = useMemo(() => visibleBiz.map((b) => [b.lat, b.lng, 0.6]), [visibleBiz]);
  const mapBounds = useMemo(() => {
    if (!mapZone) return [];
    return [...(mapZone.polygon || []), ...allBiz.map((b) => [b.lat, b.lng])];
  }, [mapZone, allBiz]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (cls !== ALL) params.classification = cls;
      if (q.trim()) params.q = q.trim();
      if (claimMode) params.standard_only = true;
      const { data } = await api.get("/shop/marketplace", { params });
      setData(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [cls, q]);

  useEffect(() => { load(); }, [cls]); // eslint-disable-line

  // Derive each zone's state from its centroid (county classifier) when no state
  // is stored, so the filter lists every state that actually has zones.
  const effState = (z) => z.state || (z.centroid ? (classifyPoint(z.centroid)?.state || null) : null);
  const itemsWithState = useMemo(() => data.items.map((z) => ({ ...z, _state: effState(z) })), [data.items]);
  const states = useMemo(() => Array.from(new Set(itemsWithState.map((z) => z._state).filter(Boolean))).sort((a, b) => a.localeCompare(b)), [itemsWithState]);
  const filtered = useMemo(() => (state === ALL ? itemsWithState : itemsWithState.filter((z) => z._state === state)), [itemsWithState, state]);

  const byState = useMemo(() => {
    const groups = {};
    for (const z of filtered) {
      const k = z._state || "Unspecified";
      (groups[k] = groups[k] || []).push(z);
    }
    return Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered]);

  const requestZone = async (z) => {
    setRequesting(z.zoneId);
    try {
      await api.post("/shop/zone-requests", { type: "zone", zone_id: z.zoneId });
      toast.success("Request submitted — an admin will review and approve it.");
      setMapZone(null);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setRequesting(null); }
  };

  const submitClaim = async () => {
    if (!claimZone) return;
    setClaiming(true);
    try {
      await api.post("/shop/zones/claim", { zone_id: claimZone.zoneId });
      setClaimZone(null);
      setMapZone(null);
      toast.success("Zone claim submitted — under review.");
      if (onClaimed) onClaimed();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setClaiming(false); }
  };

  const ActionButtons = ({ z, testPrefix }) => (
    claimMode
      ? <Button className="flex-1" onClick={() => setClaimZone(z)} data-testid={`${testPrefix}-claim-${z.zoneId}`}><Check className="w-4 h-4 mr-1.5" />Claim This Zone</Button>
      : z.requested
        ? <Button className="flex-1" variant="secondary" disabled data-testid={`${testPrefix}-requested-${z.zoneId}`}><Check className="w-4 h-4 mr-1.5" />Requested</Button>
        : <Button className="flex-1" disabled={requesting === z.zoneId} onClick={() => requestZone(z)} data-testid={`${testPrefix}-request-${z.zoneId}`}>{requesting === z.zoneId && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Request this zone</Button>
  );

  return (
    <div className="space-y-5" data-testid="marketplace-panel">
      <div>
        <h2 className="font-heading text-lg font-bold">Browse Zones</h2>
        <p className="text-sm text-muted-foreground">{claimMode ? "Pick the territory you'd like to manage, view it on the map, then claim your complimentary zone." : "Find the territory you want to operate and request it."}</p>
      </div>

      <div className="rounded-xl border border-primary/30 bg-primary/10 p-4 flex items-start gap-3" data-testid="browse-zones-notice">
        <Info className="w-5 h-5 text-primary shrink-0 mt-0.5" />
        {claimMode
          ? <p className="text-sm text-muted-foreground"><strong className="text-foreground">Claim your complimentary Founding Partner zone.</strong> Pick an available zone below, view it on the map, then tap <strong className="text-foreground">Claim This Zone</strong>. A Velopaths admin reviews every claim — your zone activates once approved.</p>
          : <p className="text-sm text-muted-foreground"><strong className="text-foreground">This is where you request zones.</strong> Pick an available zone below, view it on the map, then tap <strong className="text-foreground">Request this zone</strong>. A Velopaths admin reviews every request — once approved, you'll get an email and an on-page notice (under My Zones) with a payment link to activate the lease.</p>}
      </div>

      <ShopAreaRequest />

      <div className="flex flex-wrap gap-2 items-center" data-testid="marketplace-filters">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" placeholder="Search zone name or ID…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} data-testid="marketplace-search" />
        </div>
        <Select value={state} onValueChange={setState}><SelectTrigger className="w-[150px]" data-testid="marketplace-state"><SelectValue placeholder="All states" /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>All states</SelectItem>{states.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
        <Select value={cls} onValueChange={setCls}><SelectTrigger className="w-[150px]" data-testid="marketplace-class"><SelectValue placeholder="All classes" /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>All classes</SelectItem>{data.classifications.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
        <Button variant="outline" onClick={load} data-testid="marketplace-search-btn">Search</Button>
      </div>

      {loading ? <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        : filtered.length === 0 ? <div className="rounded-2xl border border-dashed border-border bg-card p-12 text-center" data-testid="marketplace-empty"><MapPin className="w-10 h-10 text-muted-foreground mx-auto mb-3" /><div className="font-heading font-bold">No zones available to request</div><p className="text-sm text-muted-foreground mt-1">Check back soon — Velopaths adds new territories regularly.</p></div>
        : (
          <div className="space-y-6" data-testid="marketplace-grid">
            {byState.map(([st, zones]) => (
              <div key={st} data-testid={`marketplace-state-group-${st}`}>
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="font-heading font-bold text-sm uppercase tracking-wide text-muted-foreground">{st}</h3>
                  <Badge className="bg-muted text-muted-foreground border border-border">{zones.length}</Badge>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {zones.map((z) => (
                    <div key={z.zoneId} className="rounded-2xl border border-border bg-card p-5 flex flex-col" data-testid={`marketplace-card-${z.zoneId}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div><div className="font-heading font-bold">{z.zoneName}</div>{z.bikePathName && <div className="text-xs text-primary/90 flex items-center gap-1 mt-0.5" data-testid={`marketplace-bikepath-${z.zoneId}`}><Route className="w-3 h-3" />{z.bikePathName}</div>}<div className="text-xs text-muted-foreground">{z.humanId || z.zoneId.slice(-6)}</div></div>
                        <Badge className="bg-muted text-muted-foreground border border-border">{z.classification}</Badge>
                      </div>
                      <div className="mt-3 flex items-baseline gap-2">
                        <div className="text-2xl font-heading font-extrabold text-primary">${Number(z.annualFee).toFixed(0)}</div>
                        <div className="text-xs text-muted-foreground">/year</div>
                        {z.onPromo && <Badge className="bg-rose-500/15 text-rose-300 border border-rose-500/30"><Sparkles className="w-3 h-3 mr-1" />promo</Badge>}
                      </div>
                      <div className="mt-3 grid grid-cols-3 gap-2 text-center" data-testid={`marketplace-metrics-${z.zoneId}`}>
                        <div className="rounded-lg border border-border bg-background/40 p-2"><div className="text-base font-bold text-primary">{z.prospectCount ?? 0}</div><div className="text-[10px] text-muted-foreground">Prospects</div></div>
                        <div className="rounded-lg border border-border bg-background/40 p-2"><div className={`text-base font-bold ${scoreColor(z.opportunityScore ?? 0)}`}>{z.opportunityScore ?? 0}</div><div className="text-[10px] text-muted-foreground">Opp. Score</div></div>
                        <div className="rounded-lg border border-border bg-background/40 p-2"><div className="text-base font-bold text-emerald-400">{fmtMoney(z.projectedMonthly)}</div><div className="text-[10px] text-muted-foreground">Est/mo</div></div>
                      </div>
                      <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
                        <span data-testid={`marketplace-adv-${z.zoneId}`}>{z.advertiserCount} advertiser{z.advertiserCount === 1 ? "" : "s"}</span>
                      </div>
                      <div className="mt-4 flex gap-2">
                        <Button variant="outline" className="flex-1" onClick={() => setMapZone(z)} data-testid={`marketplace-viewmap-${z.zoneId}`} disabled={(z.polygon || []).length < 3}><MapPin className="w-4 h-4 mr-1.5" />View on map</Button>
                        <ActionButtons z={z} testPrefix="marketplace" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

      <Dialog open={!!mapZone} onOpenChange={(o) => !o && setMapZone(null)}>
        <DialogContent className="w-[95vw] max-w-5xl md:max-w-[50vw]">
          <DialogHeader>
            <DialogTitle data-testid="marketplace-map-title">
              {mapZone?.zoneName}
              <span className="text-muted-foreground font-normal"> · {bizLoading ? "…" : `${zoneBiz?.total ?? 0} business${(zoneBiz?.total ?? 0) === 1 ? "" : "es"}`}{(zoneBiz?.city || zoneBiz?.state || mapZone?.state || mapZone?._state) ? ` · ${[zoneBiz?.city, zoneBiz?.state || mapZone?.state || mapZone?._state].filter(Boolean).join(", ")}` : ""}</span>
            </DialogTitle>
          </DialogHeader>
          {mapZone && (mapZone.polygon || []).length >= 3 && (
            <div className="space-y-3" data-testid="marketplace-map-dialog">
              <div className="relative rounded-xl overflow-hidden" data-testid="marketplace-map-wrap">
                <LeafletMap height={460} center={polygonCentroid(mapZone.polygon)}>
                  <FitBounds bounds={mapBounds.length >= 2 ? mapBounds : mapZone.polygon} nonce={`${mapZone.zoneId}-${allBiz.length}`} />
                  <Polygon positions={mapZone.polygon} pathOptions={{ color: "#EF4444", fillColor: "#FACC15", fillOpacity: 0.25, weight: 2 }} />
                  {heatOn && <HeatLayer points={heatPoints} enabled={heatOn} />}
                  {visibleBiz.length > 0 && <BusinessLayers businesses={visibleBiz} readOnly />}
                </LeafletMap>
                <BusinessLayerToggles restaurantsOnly={false} layers={layers} setLayers={setLayers} heatOn={heatOn} setHeatOn={setHeatOn} bizCounts={bizCounts} bizLoading={bizLoading} bizStatus={null} hideBikeFilter />
              </div>
              {(zoneBiz?.missingCount ?? 0) > 0 && (
                <div className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid="marketplace-map-missing">
                  <Info className="w-3.5 h-3.5 shrink-0" />{zoneBiz.missingCount} business{zoneBiz.missingCount === 1 ? "" : "es"} not shown on map due to missing location
                </div>
              )}
              <div className="flex items-center justify-between gap-3">
                <div className="text-sm"><span className="font-bold text-primary">${Number(mapZone.annualFee).toFixed(0)}</span><span className="text-muted-foreground">/year · {mapZone.classification}</span></div>
                <div className="w-48"><ActionButtons z={mapZone} testPrefix="marketplace-map" /></div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={!!claimZone} onOpenChange={(o) => !o && setClaimZone(null)}>
        <DialogContent className="max-w-md" data-testid="claim-confirm-dialog">
          <DialogHeader><DialogTitle>Claim Zone</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="font-heading text-lg font-bold">{claimZone?.zoneName}</div>
            <p className="text-sm text-muted-foreground">Are you sure you'd like to claim this complimentary Founding Partner zone? Your request will be reviewed before activation.</p>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setClaimZone(null)} data-testid="claim-cancel">Cancel</Button>
              <Button onClick={submitClaim} disabled={claiming} data-testid="claim-submit">{claiming && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Submit Claim</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
